import { HeraldApplicationResponse } from 'clients/types';
import { AIFeature } from 'enums';
import { usePagination } from 'hooks';
import { messages } from 'i18n';
import { TeamInsuranceProduct } from 'types';
import ApiNotReadyBanner from 'broker/pages/SubmissionWorkspacePage/components/ApiNotReadyBanner';
import InnerPageLayout from 'broker/pages/SubmissionWorkspacePage/components/InnerPage/InnerPageLayout';
import { InnerPageProps } from 'broker/pages/SubmissionWorkspacePage/components/InnerPage/types';
import { AIContextProvider } from 'broker/pages/SubmissionWorkspacePage/contexts/ai/AIContextProvider';
import useIsToolboxOpen from 'broker/pages/SubmissionWorkspacePage/hooks/useIsToolboxOpen';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import { isIncompleteApiApplication } from 'broker/utils';
import { Footer } from './Components/Footer';
import HeaderBottomSideContent from './Components/HeaderBottomSideContent';
import { Products } from './Components/Products';
import { useProductFilters } from './useProductFilters';
import { useTeamProductsState } from './useTeamProductsState';

export interface AddProductsProps {
  onTeamProductsSelected: (teamProducts: TeamInsuranceProduct[]) => Promise<void>;
  teamProducts: TeamInsuranceProduct[];
  isLoading: boolean;
  heraldApplicationResponse?: HeraldApplicationResponse;
}

export default function AddProducts({
  onTeamProductsSelected,
  teamProducts,
  isLoading,
  heraldApplicationResponse,
}: AddProductsProps) {
  const isToolboxOpened = useIsToolboxOpen();
  const { partialSubmission } = useSubmissionsWorkspace();
  const { page, setPage, rowsPerPage, setRowsPerPage, paginationReset } = usePagination();

  const productsFilters = useProductFilters(teamProducts, isLoading, paginationReset, true);

  const { selectedTeamProducts, onProductSelected, onProductRemoved } = useTeamProductsState();

  return (
    <InnerPageLayout
      title={messages.addProductsPage.title}
      subTitle={messages.addProductsPage.subtitle}
      sx={{ paddingX: 0, paddingTop: 1, paddingBottom: 0 }}
      headerBottomContent={() => (
        <HeaderBottomSideContent isToolboxOpened={isToolboxOpened} productsFilters={productsFilters} />
      )}
      headerTopContent={isIncompleteApiApplication(partialSubmission) ? () => <ApiNotReadyBanner /> : undefined}
      footer={(innerPageProps: InnerPageProps) => (
        <Footer
          innerPageProps={innerPageProps}
          selectedTeamProducts={selectedTeamProducts}
          onTeamProductsSelected={onTeamProductsSelected}
          partialSubmission={partialSubmission}
        />
      )}
    >
      {() => (
        <AIContextProvider feature={AIFeature.ProductRecommendation}>
          <Products
            heraldApplicationResponse={heraldApplicationResponse}
            items={teamProducts}
            isLoadingTeamProducts={isLoading}
            sortedAndFilteredItems={productsFilters.sortedAndFilteredItems}
            search={productsFilters.search}
            setPage={setPage}
            page={page}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            selectedTeamProducts={selectedTeamProducts}
            onTeamProductSelected={onProductSelected}
            onTeamProductRemoved={onProductRemoved}
          />
        </AIContextProvider>
      )}
    </InnerPageLayout>
  );
}
